import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { getLabelFallback, convertNumber, getTotalTax, getStringMonth, handleHTTPError } from '@utils/utils';
import { updateShippingInfo, deletePromoCode } from '@utils/services';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';

import RadioButton from '@components/RadioButton';
import Insurance from '@components/Insurance';
import InstalmentsModal from '@components/InstalmentsModal';
import InputElement from '@components/InputElement';
import * as actions from '@redux-actions/';
import ApplePayButton from './ApplePayButton';

import ApplePay from '@images/svg-icons/applepay.svg';
import PayPal from '@images/svg-icons/paypal.svg';
import Delete from '@images/svg-icons/delete.svg';
import InfoBold from '@images/svg-icons/info_bold.svg';
import Close from '@images/svg-icons/close.svg';
import PayPalButton from './PayPalButton';
import Truck from '@images/svg-icons/truck.svg';
import SimpleElementSkeleton from './skeletons/SimpleElementSkeleton';

import BossApp from '@components/Boss/BossApp';
import { Checkbox } from '@mui/material';

type ShippingProps = {
    checkoutClick: () => void;
    hasSmartGlasses: boolean;
    smartGlassesError: string;
    hasSafetyGlasses: boolean;
    seeSpeedyCheckbox: boolean;
}

const Shipping = ({ checkoutClick, hasSmartGlasses, smartGlassesError, hasSafetyGlasses, seeSpeedyCheckbox} : ShippingProps) : ReactElement => {
    const dispatch = useDispatch();
    const ref = useRef(null);

    const config = useSelector(state => state.cart.config);
    const enabledShippingModes = useSelector(state => state.cart.enabledShippingModes);
    const cart = useSelector(state => state.cart.cart);
    const selectedShippingMode = useSelector(state => state.cart.selectedShippingMode);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const isMobile = useSelector(state => state.cart.isMobile);
    
    const [isShippingTimingTooltipVisible, setShippingTimingTooltipVisible] = useState(false);
    const [showInstalmentsModal, setShowInstalmentsModal] = useState(false);
    const [showTaxCalculate, setShowTaxCalculate] = useState(false);
    const [showTaxCalculateLabel, setShowTaxCalculateLabel] = useState(getLabelFallback('CALCULATE_TAX_BUTTON_LABEL'));

    const [shipModeId, setShipModeId] = useState(null);
    const [showApplePay, setShowApplePay] = useState(false);
    
    const [showSpeedyTooltip, setShowSpeedyTooltip] = useState(false);
    const speedyOrderDetails = useSelector(state => state.cart.speedyOrderDetails);
    const [isOrderSpeedyErrorActive, setIsOrderSpeedyErrorActive] = useState(false);

    const methods = useForm<any>();

    const submitZipCode = (data) => {
        setShowTaxCalculate(false);
        setShowTaxCalculateLabel(getLabelFallback('RECALCULATE_TAX_BUTTON_LABEL'));

        //TODO probabilmente qua ci sarà una nuova chiamata e poi la get del cart
    }

    const changeShippingSelectedMode = async (value : string) => {
        const newMode = enabledShippingModes.find(mode => mode.shipModeCode === value);
        if(!!newMode && !!newMode.shipModeId) {
            if(newMode.shipModeCode !== 'Ship to store') {
                dispatch(actions.startLoading());
                dispatch(actions.setIsBossSelected(false));
                var parameters = {"calculationUsage": "-1,-2,-3,-4,-5,-6,-7", "shipModeId": newMode.shipModeId}
                if(newMode.shipModeCode === 'Speedy RX'){
                    const speedyProducts = speedyOrderDetails?.glassesDetails?.filter(item => item?.speedyEligible);
                    let speedyComboItems = []
                    for (const item of speedyProducts) {
                        speedyComboItems.push(item.frameOrderItemId)
                    }
                    parameters.speedyComboItems = speedyComboItems;
                }
                await updateShippingInfo(
                    config.hostname,
                    parameters
                ).then(() => {
                    dispatch(actions.getCart(config.mockRequests, config.hostname));
                    dispatch(actions.getSpeedyDetails(config.hostname, cart.orderId));
                    dispatch(actions.setSelectedShippingMode(newMode));
                }).catch(err => {
                    console.error('>>> Update shipping info => error status:', err.status, ', error: ', err);
                    handleHTTPError(dispatch, actions, err);
                })
            } else {
                dispatch(actions.setSelectedShippingMode(newMode));
            }
            
        }
    }

    const getShippingModes = () => {
        const isPlano = formattedOrderItems.find(item => (item.frame.xitem_frameType  === 'RX' || item.frame.xitem_frameType  === 'NO PRESCRIPTION')) === undefined ? true : false;

        return config.enabledShippingModes.map(e => {
            const mode = enabledShippingModes.filter(mode => mode.shipModeCode === e)[0];
            
            if(!!mode) {
                if(
                    ((hasSmartGlasses === true && mode.shipModeCode === 'Standard Delivery' && !isPlano)
                    ||
                    (hasSmartGlasses === true && mode.shipModeDescription.toLowerCase() === 'collect in store' && !isPlano)
                    ||
                    (hasSmartGlasses && isPlano) ||
                    !hasSmartGlasses) &&
                    !(hasSmartGlasses === false && mode.shipModeCode === 'Standard Delivery')
                    && !(hasSmartGlasses && isPlano && mode.shipModeCode === 'Standard Delivery')
                    && mode.shipModeDescription.toLowerCase() != 'speedy rx'
                ) {

                    let ld;
                    let hd;

                    if (hasSmartGlasses && mode.shipModeDescription.toLowerCase() === 'collect in store'){
                        ld = isPlano ? mode.lowEstimatedDeliveryDateStellaPlano : mode.lowEstimatedDeliveryDateStellaRx;
                        hd = isPlano ? mode.highEstimatedDeliveryDateStellaPlano : mode.highEstimatedDeliveryDateStellaRx;
                    } else {
                        ld = isPlano ? mode.lowEstimatedDeliveryDatePlano : mode.lowEstimatedDeliveryDateRx;
                        hd = isPlano ? mode.highEstimatedDeliveryDatePlano : mode.highEstimatedDeliveryDateRX;
                    }
                    const lowDate = new Date(ld.replace('CEST', 'UTC').replace('CET', 'UTC'));
                    const highDate = new Date(hd.replace('CEST', 'UTC').replace('CET', 'UTC'));
                    const lowMonth = getStringMonth(lowDate.getMonth()).substring(0,3);
                    const highMonth = getStringMonth(highDate.getMonth()).substring(0,3);
                    
                    let lowDay = lowDate.getDate().toString();
                    lowDay = lowDay.length === 1 ? '0' + lowDay : lowDay;
    
                    let highDay = highDate.getDate().toString();
                    highDay = highDay.length === 1 ? '0' + highDay : highDay;
    
                    let hasTooltip = false;
                    let toolTipText = '';
                    let icon = null;
    
                    if(mode.shipModeCode === 'Green Delivery') {
                        hasTooltip = true;
                        toolTipText = getLabelFallback('GREEN_DELIVERY_TOOLTIP_LABEL');
                        icon=<Truck />
                    }

                    const selectedShipping = (hasSmartGlasses === true && mode.shipModeCode === 'Standard Delivery' && selectedShippingMode.shipModeCode !== 'Ship to store' && !isPlano) ? mode.shipModeCode : selectedShippingMode.shipModeCode;
    
                    const isBoss = mode.shipModeDescription.toLowerCase() === 'collect in store';
                    if (isBoss && !shipModeId) {
                        setShipModeId(mode.shipModeId);
                    }

                    return ( 
                        <React.Fragment key={mode.shipModeCode.replace(/ /g, '_')}>
                            <RadioButton  
                                id={mode.shipModeCode.replace(/ /g, '_')}
                                checked={selectedShipping === mode.shipModeCode}
                                label={getLabelFallback(mode.shipModeDescription.toUpperCase().replace(/ /g, '_') + '_LABEL')}
                                value={mode.shipModeCode}
                                text={
                                    <>
                                        <div className="radio-button-center-range">{getLabelFallback('ESTIMATED_DELIVERY_DATE_LABEL')} <span>{lowMonth + ' ' + lowDay + ' - ' + highMonth + ' ' + highDay}</span></div>
                                        <div className="radio-button-center-days">{mode.field1}</div>
                                    </>
                                }
                                rightText={mode.field2 === '0.00' ? getLabelFallback('FREE_SHIPPING_LABEL') : getLabelFallback('CURRENCY_LABEL') + ' ' + convertNumber(mode.field2)}
                                onChange={(e)=> {changeShippingSelectedMode(e.currentTarget.value); }}
                                data-analytics_available_call='0'
                                hasTooltip= {hasTooltip}
                                toolTipText= {toolTipText}
                                icon={icon}
                                collectInStore={isBoss}
                            />
                        </React.Fragment>
                    );
                }
            }
        });
    }
    
    const shippingTimingClick = () => {
        setShippingTimingTooltipVisible(isShippingTimingTooltipVisible => isShippingTimingTooltipVisible = !isShippingTimingTooltipVisible);
    }

    const shippingTimeAndPriceSpeedy = () => {
        const isPlano = formattedOrderItems.find(item => (item.frame.xitem_frameType  === 'RX' || item.frame.xitem_frameType  === 'NO PRESCRIPTION')) === undefined ? true : false;
        const speedy = enabledShippingModes.filter(mode => mode.shipModeCode.toLowerCase() === 'speedy rx')[0];

        if(!!speedy){
            let ld = speedy.lowEstimatedDeliveryDateRx;
            // let hd = speedy.highEstimatedDeliveryDateRX;

            const lowDate = new Date(ld.replace('CEST', 'UTC').replace('CET', 'UTC'));
            // const highDate = new Date(hd.replace('CEST', 'UTC').replace('CET', 'UTC'));
            const lowMonth = getStringMonth(lowDate.getUTCMonth()).substring(0,3);
            // const highMonth = getStringMonth(highDate.getUTCMonth()).substring(0,3);
            
            let lowDay = lowDate.getUTCDate().toString();
            lowDay = lowDay.length === 1 ? '0' + lowDay : lowDay;

            // let highDay = highDate.getUTCDate().toString();
            // highDay = highDay.length === 1 ? '0' + highDay : highDay;

            return (
                <>
                    <div className='speedy-rx-shipping-select-textcontainer-label'>
                        <div className='speedy-rx-shipping-select-textcontainer-label-container'>
                            <span className='speedy-rx-shipping-select-textcontainer-label-container-title' id='2-day-speedy-delivery'>{getLabelFallback('SPEEDY_RX_CHECK_TITLE')}</span>
                            <div className='speedy-rx-shipping-select-textcontainer-label-container-tooltip'>
                                <button className={`speedy-rx-shipping-select-textcontainer-label-container-tooltip-info ${showSpeedyTooltip === true ? 'open-tip' : ''}`} aria-label={'Open tooltip'} onClick={() => setShowSpeedyTooltip(showSpeedyTooltip => showSpeedyTooltip = !showSpeedyTooltip)}>
                                    <InfoBold/>
                                </button>
                                {!!showSpeedyTooltip && (
                                    <div className='speedy-rx-shipping-tooltip'>
                                        <div className='speedy-rx-shipping-tooltip-title-and-close'>
                                            <span className='speedy-rx-shipping-tooltip-title-and-close-label'>
                                                {getLabelFallback('SPEEDY_RX_TOOLTIP_TITLE')}
                                            </span>
                                            <button aria-label={'Close tooltip'} onClick={() => setShowSpeedyTooltip(showSpeedyTooltip => showSpeedyTooltip = !showSpeedyTooltip)}><Close /></button>
                                        </div>
                                        <div className='speedy-rx-shipping-tooltip-text'>
                                            <span className='speedy-rx-shipping-tooltip-text-content'>
                                                {getLabelFallback('SPEEDY_RX_TOOLTIP_TEXT')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <span className='speedy-rx-shipping-select-textcontainer-label-subtitle'>
                            {getLabelFallback('SPEEDY_RX_CHECK_SUBTITLE') + ' ' + lowMonth + ' ' + lowDay} 
                        </span>                 
                    </div>
                    {selectedShippingMode.shipModeCode != speedy.shipModeCode && (
                        <div className='speedy-rx-shipping-select-textcontainer-price'>
                            <span className='speedy-rx-shipping-select-textcontainer-price-tag'>
                                {getLabelFallback('CURRENCY_LABEL') + parseFloat(speedy.field2)}
                            </span>
                            <span className='speedy-rx-shipping-select-textcontainer-price-label'>
                                {getLabelFallback('SPEEDY_RX_PRICE_LAVEL')}
                            </span>
                        </div>
                    )}
                </>
            )
        }
    }

    const handleSpeedyShip = () => {
        if(!!selectedShippingMode && selectedShippingMode.shipModeCode.toLowerCase() != 'speedy rx'){
            changeShippingSelectedMode('Speedy RX')
        }else{
            changeShippingSelectedMode('Green Delivery')
        }
    }

    /* check if adjustment derives from promoCode */
    const isPromoCode = (itemAdjustment) =>{
        let filteredAdj=[];
        for (let i = 0; i < itemAdjustment.length; i++) {
            let promoId=itemAdjustment[i].code;
            if(cart.promotionCode!=undefined){
                for (let j = 0; j < cart.promotionCode.length; j++) {
                    let promoCodeid=cart.promotionCode[j].associatedPromotion[0].promotionId
                    if(promoId.includes(promoCodeid)){
                        filteredAdj.push(itemAdjustment[i]);
                    }
                }
            }else{
                filteredAdj=itemAdjustment.filter(code => (code.displayLevel === 'Order' ));
            }

        }
        return filteredAdj;
    }

    const filterPromoCodes = (codes) => {
        codes=codes.filter(code => (
            code.code.search('INSADJ') === -1))
        return isPromoCode(codes);
    }

    const getPromoCodesElements = ( codes ) => {
        const orderLevelCodes = filterPromoCodes(codes);

        return orderLevelCodes.map((c, index) => {
            const indexEl = cart.promotionCode.findIndex(p => c.code.indexOf(p.associatedPromotion[0].promotionId) !== -1);
            const idMe = codes.find(code => code.code?.search('IDME') !== -1);
            if(!!idMe) return <></>
            
            return(                
                <div key={"promo-code-" + index} className="shipping-container-promo-codes-item">
                    <div className="shipping-container-promo-codes-item-left">
                        <button aria-label={getLabelFallback('DELETE_PROMO_CODE_LABEL')} className="shipping-container-promo-codes-item-button" onClick={() => {removePromoCode(cart.promotionCode[indexEl].code)}}><Delete /></button>
                        <label className="shipping-container-promo-codes-item-label">{getLabelFallback('PROMO_LABEL_SHIPPING')} {cart.promotionCode[indexEl].code}</label>
                    </div>
                    <div className="shipping-container-promo-codes-item-right">
                        -{getLabelFallback('CURRENCY_LABEL')}{(convertNumber((c.amount) * (-1)))}
                    </div>
                </div>
            )
        })
    }

    const getIdMePromoCode = ( codes ) => {
        const idMe = codes.find(code => code.code?.search('IDME') !== -1);
        if(!idMe) return <></>

        return (
            <div className="shipping-container-promo-codes-item">
                <div className="shipping-container-promo-codes-item-left">
                    <button aria-label={getLabelFallback('DELETE_PROMO_CODE_LABEL')} className="shipping-container-promo-codes-item-button" onClick={() => {removePromoCode(idMe.code)}}><Delete /></button>
                    <label className="shipping-container-promo-codes-item-label">{getLabelFallback('EXTRA_DISCOUNT_LABEL')}</label>
                </div>
                <div className="shipping-container-promo-codes-item-right">
                    -{getLabelFallback('CURRENCY_LABEL')}{(convertNumber((idMe.amount) * (-1)))}
                </div>
            </div>
        )
    }

    const removePromoCode = async (code) => {
        dispatch(actions.startLoading());
        await deletePromoCode(config.hostname, code)
            .then(() => {
                dispatch(actions.getCart(config.mockRequests, config.hostname));
                dispatch(actions.setShowPromoCodeFeedback(false));
            })
            .catch(err => {
                console.error('>>> Delete promo code => error status:', err.status, ', error: ', err);
                handleHTTPError(dispatch, actions, err);
                window?.tealium_data2track?.push({
                    'tealium_event': 'OrderDiscountCode-Tentative',
                    'id': 'Event',
                    'Events_DiscountTentative' : '1',
                    'Order_DiscountCode': code,
                    'Order_DiscountName': '',
                    'Error_Code': err.data.errors[0].errorMessage.toString(),
                    'Error_Source': 'user',
                    'Tracking_Type': ''
                });
            })
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setShippingTimingTooltipVisible(false);
            }
        };
        document.addEventListener('click', handleClickOutside, true);

        console.log(config.applepay.merchantIdentifier);
        if (window.ApplePaySession) {
            if (config.applepay.merchantIdentifier != null) {
                var promise = ApplePaySession.canMakePaymentsWithActiveCard(config.applepay.merchantIdentifier);
                promise.then(function (canMakePayments) {
                    // alert("canMakePayments for " + config.applepay.merchantIdentifier + " ? " + canMakePayments)
                    if (canMakePayments) {
                        setShowApplePay(true);
                    }
                }, function (reject) {
                    console.log("canMakePaymentsWithActiveCard promise rejected");
                });
            }
        } else {
            console.log("Apple Pay non supportata")
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    useEffect(() => {
        if(config.isSpeedyRxEnabled === 'true' && !!speedyOrderDetails){
            if(!!speedyOrderDetails.orderSpeedyActive && speedyOrderDetails.mixedCart === true){
                setIsOrderSpeedyErrorActive(true);
            } else {
                setIsOrderSpeedyErrorActive(false);
            }
        }
    }, [speedyOrderDetails])

    useEffect (() => {
        if(!!formattedOrderItems && !!selectedShippingMode && selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx'){
            const numberOfSpeedy = speedyOrderDetails?.glassesDetails?.filter(item => item?.speedyEligible).length;

            if(numberOfSpeedy < 1){
                changeShippingSelectedMode('Green Delivery');
            }
        }
    }, [formattedOrderItems])

    return(
        <div className="shipping-container">
            <div className="shipping-container-top">
                <label className="shipping-container-top-title">{ getLabelFallback('SHIPPING_BOX_TITLE') }</label>
                <div ref={ref}>
                    <button data-analytics_available_call='0' className="primary-button" onClick={() => {shippingTimingClick()}}>{ getLabelFallback('SHIPPING_TIME_LABEL') }</button>
                    {isShippingTimingTooltipVisible &&
                        <div className="shipping-timing-tooltip">
                            <div className="shipping-timing-tooltip-header">{ getLabelFallback('SHIPPING_TIME_TOOLTIP_HEADER') }</div>
                            <ul className="shipping-timing-tooltip-options">
                                <li>
                                    <span className="shipping-timing-tooltip-options-label">{ getLabelFallback('REQUIRED_PRESCRIPTION_LABEL') }</span>
                                    <span className="shipping-timing-tooltip-options-text">{ getLabelFallback('REQUIRED_PRESCRIPTION_TEXT') }</span>
                                </li>
                                <li>
                                    <span className="shipping-timing-tooltip-options-label">{ getLabelFallback('NO_PRESCRIPTION_LABEL') }</span>
                                    <span className="shipping-timing-tooltip-options-text">{ getLabelFallback('NO_PRESCRIPTION_TEXT') }</span>
                                </li>
                            </ul>
                        </div>
                    }
                </div>
                
            </div>
            
            {!!hasSmartGlasses && 
                <div className="shipping-container-stella">
                    <span className="stella-puerto-rico">{ getLabelFallback('SHIPPING_BOX_STELLA_ALERT') }</span>
                </div>
            }

            <div className="shipping-container-mode">
                {config.isSpeedyRxEnabled === 'true' && !!selectedShippingMode && !!formattedOrderItems && !!seeSpeedyCheckbox && (
                        <>
                            <div className={`speedy-rx-shipping ${selectedShippingMode.shipModeCode.toLowerCase() != 'speedy rx' && 'unchecked'}`}>
                                <div className='speedy-rx-shipping-title'>
                                    <span>{selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx' ? getLabelFallback('SPEEDY_RX_INFO') : getLabelFallback('SPEEDY_RX_INFO_NOT_SELECTED')}</span>
                                </div>
                                <div className='speedy-rx-shipping-select'>
                                    <Checkbox
                                        className={`speedy-rx-shipping-select-checkbox ${selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx' && 'checked'}`}
                                        checked={selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx'}
                                        onChange={() => handleSpeedyShip()}
                                        color="primary"
                                        inputProps={{ 'aria-labelledby' : '2-day-speedy-delivery'}}
                                    />
                                    <div className='speedy-rx-shipping-select-textcontainer'>
                                        {shippingTimeAndPriceSpeedy()}
                                    </div>
                                </div>
                            </div>
                        </>
                    )
                }

                {!!selectedShippingMode && !!formattedOrderItems ? 
                    !!selectedShippingMode.shipModeCode && selectedShippingMode.shipModeCode.toLowerCase() === 'speedy rx' ? 
                    <></> : getShippingModes() :
                    !!config && !!config.enabledShippingModes ? config.enabledShippingModes.map((e, index) => {
                        return <div key={'skeleton-shipping-mode'+index} style={{marginBottom: '8px'}}>
                                    <SimpleElementSkeleton width={'100%'} height={'20px'} />
                                </div>
                    }) :
                    <></>          
                }

                {!!selectedShippingMode && 
                    selectedShippingMode.shipModeCode.toLowerCase() === 'ship to store' &&
                    config.bossApp.enabled === true && (
                    <div className="shipping-container-boss">
                        <BossApp config={config} shipModeId={shipModeId} hasSafetyGlasses={hasSafetyGlasses} hasSmartGlasses={hasSmartGlasses}></BossApp>
                    </div>
                )}
            </div>
            <div className="shipping-container-insurance hide-mobile">
                {!isMobile && <Insurance id={"insurance-desktop"}/>}
            </div>
            {/* TODO: RESTORE WHEN CALCULATE TAX IS AVAILABLE
            <div className="shipping-container-tax">
                <div className="shipping-container-tax-top">
                    <div className="shipping-container-tax-top-header">
                        <span className="shipping-container-tax-label">{ getLabelFallback('ESTIMATED_TAX_LABEL') }</span>
                        <button aria-label={ showTaxCalculateLabel } className="shipping-container-tax-link primary-button" 
                            onClick={() => { setShowTaxCalculate(showTaxCalculate => showTaxCalculate = !showTaxCalculate) }}
                            data-element-id='Summary_CalculateTax'
                        >
                            { showTaxCalculateLabel }
                        </button>
                    </div>
                    <span>{getTotalTax(cart)}</span>
                </div>
                
                {showTaxCalculate &&
                    <FormProvider {...methods}>
                        <form className="shipping-container-tax-form"
                            onSubmit={methods.handleSubmit(submitZipCode)}
                        >
                            <InputElement classes="small" id="zip-code-tax" type="text" placeholder={getLabelFallback('CALCULATE_TAX_PLACEHOLDER')} required={true}/>
                            <button type="submit" className="violet-button">{ getLabelFallback('CALCULATE_TAX_BUTTON_LABEL') }</button>
                        </form>
                    </FormProvider>
                    
                }
            </div> */}
            {!!cart && cart.adjustment?.length > 0 && filterPromoCodes(cart.adjustment).length > 0 &&
                <div className="shipping-container-promo-codes">
                    {getPromoCodesElements(cart.adjustment)}
                    {getIdMePromoCode(cart.adjustment)}
                </div>
            }
            
            <div className="shipping-container-payment">
                <div className="shipping-container-payment-top">
                    <div className="shipping-container-payment-top-text">
                        <div className="shipping-container-payment-top-text-total">
                            { getLabelFallback('TOTAL_LABEL') } 
                            <span>
                                ({getTotalTax(cart) === '--' ? getLabelFallback('TAX_EXCLUDED_LABEL') : getLabelFallback('TAX_INCLUDED_LABEL') })
                            </span>
                        </div>
                        <span>
                            {
                                config.klarna.enabled === true && config.klarna.InstallmentEnabled === true ? getLabelFallback('PAYMENT_METHODS_CLAIM') : getLabelFallback('PAYMENT_METHODS_CLAIM_NO_KLARNA')
                                
                            } 
                            <button aria-label={ getLabelFallback('LEARN_MORE_PAYMENT_LABEL') }
                                className="primary-button" 
                                onClick={() => {setShowInstalmentsModal(true)}}
                                data-element-id='Prod_PayOverTime'
                            >
                                { getLabelFallback('LEARN_MORE_PAYMENT_LABEL') }
                            </button>
                        </span>

                        {showInstalmentsModal && 
                            <InstalmentsModal close={ () => setShowInstalmentsModal(false)}/>
                        }
                    </div>
                    <span className="shipping-container-payment-top-total">{getLabelFallback('CURRENCY_LABEL')}{convertNumber(cart.grandTotal)}</span>
                </div> 
                
                {/* {hasSmartGlasses && 
                    <div className="shipping-container-minimum">
                        {getLabelFallback('SMART_GLASSES_MINIMUM_YEARS_OLD_LABEL')}
                    </div>
                } */}

                <button data-element-id="Summary_Checkout" onClick={() =>{checkoutClick()}} 
                    className={`violet-button ${smartGlassesError.length > 0 || (!!selectedShippingMode && selectedShippingMode.shipModeDescription.toLowerCase() === 'collect in store' && !!cart && !!cart.orderItem[0] && !cart.orderItem[0].xitem_storeIdentifier) ? 'disabled' : ''} ${!!isOrderSpeedyErrorActive ? 'disabled' : ''}`}>{ getLabelFallback('CHECKOUT_LABEL') }</button>
                <div className="shipping-container-payment-bottom">
                    {!!selectedShippingMode &&
                        selectedShippingMode.shipModeCode.toLowerCase() !== 'ship to store' &&
                        <>
                            <div className="shipping-container-payment-bottom-label">{getLabelFallback('PAYMENT_METHODS_LABEL')}</div>
                            <div className="shipping-container-payment-bottom-buttons">
                                <PayPalButton onApproveUrl={config.paypal.PAYPAL_EXPRESS_CHECKOUT_URL} onReturnUrl={config.paypal.PAYPAL_RETURN_URL_CART} smartGlassesError={smartGlassesError} />
                                {showApplePay && config.applepay.enabled === true && (
                                    <ApplePayButton expressCheckout={true} smartGlassesError={smartGlassesError}/>
                                )
                                }
                            </div>
                        </>
                    }
                    <div className="shipping-container-payment-bottom-terms">
                        <span dangerouslySetInnerHTML={{__html: getLabelFallback('RIGHTS_LABEL')
                            .replace('{SITE_NAME}', getLabelFallback('SITE_NAME'))
                            .replace('{TERMS_AND_CONDITION}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('TERMS_CONDITION_LABEL')+'" href="'+config.linkTermCondition+'">'+ getLabelFallback('TERMS_CONDITION_LABEL')+'</a>')
                            .replace('{PRIVACY_POLICY}', '<a class="primary-button" target="_blank" aria-label="'+getLabelFallback('PRIVACY_LABEL')+'" href="'+config.linkPrivacyPolicy+'">'+ getLabelFallback('PRIVACY_LABEL')+'</a>')}}>
                        </span>
                    </div>
                    <div className="shipping-container-payment-bottom-fsa">{ getLabelFallback('FSA_LABEL') }</div>
                </div>
            </div>
        </div>
    )
};

export default Shipping;
